import { calcTotals, quoteLineGroups, lineGroupNotesFrom } from "./builderHelpers.js";
import { initSections, mkClientCode, sortSessions, sessionHasCaptureData, fmtMoney } from "./utils.js";
import { hasProBonoDiscount, normalizeBilling } from "./proBono.js";
import { normalizePrintWording } from "./printWording.js";
import { SECTION_DEFS } from "./constants.js";

export const SCHEMA_VERSION = 2;

// quoteDate is captured for audit/display but intentionally not restored onto the active quote.
export const QUOTE_FIELDS = [
  // lineGroups (2026-09-04): the block list is quote content, so a version carries it and
  // a restore brings the version's blocks back, empty ones included. lineGroupNotes is not
  // versioned on its own; it is re-derived from the restored list so the two cannot disagree.
  "lineItems", "lineGroups", "quoteDiscounts", "summary",
  "deliverableText", "deliverableFormats", "turnaround", "turnaroundTBD", "turnaroundUnit", "lidarDetected",
  "requiresDeposit", "deposit", "includePricingSheet", "sections",
  "sessions", "multiDay", "scanDate", "scanDateTBD",
  // Holds (2026-09-04): a restored schedule carries its own assignments. holdOff
  // is deliberately NOT versioned; the switch is current state, not quote content.
  "equipmentIds", "crew",
  "jobLocation", "locationVenue", "locationAddress", "locationCity", "locationState", "locationZip",
  // contactEmail (2026-10-26) travels with contactName/contactEntityId so a restore
  // brings back one person; a version from before it gets the email from the CRM
  // record (restoreContactFields).
  // contactPhone and billing (2026-11-03, docs/quotes/quote-saving.md) were missing, so a
  // restore kept today's values beside the version's person and discounts. A version from
  // before them has neither; see applyVersionToQuote and restoreContactFields for what
  // that restores.
  "isStudio", "clientName", "contactName", "contactEmail", "contactPhone", "contactEntityId", "companyEntityId", "productionName",
  "billing",
  // Both follow the client (2026-11-02, AI box). A version from before them has neither,
  // so it restores none: an approved code stays with the client it was approved for.
  "approvedClientCode", "clientUnlinked",
  // Custom wording restores with its version. A version with none restores generated
  // wording, as an explicit null per key, because saves merge (lib/printWording.js).
  "printWording", "printWordingMoneyKey",
  "industry", "leadSource", "referredBy", "internalNotes", "emailText", "notesText",
  // Where the notes came from (2026-10-09, AI Start discounts): restored with them, so
  // client text cut at a bare "Notes" line cannot come back as operator notes. A
  // version from before the field restores "" (origin unknown, so its notes do not
  // count for a discount line until the operator edits them; ai.js notesAreOperator).
  "notesSource",
  "attachments", "quoteDate", "docType",
  // contactMatches is intentionally NOT versioned: CRM contact-link decisions are
  // a current-state triage step, not client-facing quote content (owner call,
  // 2026-06-01). The versioned identity fields (clientName/contactName/
  // contactEntityId/companyEntityId) carry the durable outcome.
];

const PRESERVE_FIELDS = [
  // Preserved only when present on the current quote; restore does not synthesize absent audit fields.
  "id", "createdAt", "savedAt", "deletedAt", "draft", "jobStatus", "fromAssessment",
  "changeOrderOfJobId", "changeOrderOfQuoteId", "versions", "followUpHistory",
  "clientCode", "_docId",
];

const SESSION_COMMERCIAL_FIELDS = [
  "id", "sessionType", "date", "dayType", "callTime", "wrapTime", "notes",
  "location", "locationVenue", "locationAddress", "locationCity", "locationState", "locationZip",
  "jobLocation", "isStudio",
];

const OPERATIONAL_SESSION_FIELDS = [
  "captureLog", "captureNotes", "issues", "actualArrival", "actualWrap", "travelTime",
  "peoplePresent", "clientContacts", "gcalEventId", "gcalSyncedFingerprint",
];

export function mkVersionId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `qv_${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
    }
  } catch (_) { /* crypto.randomUUID unavailable */ }
  return `qv_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
}

const clone = (value, fallback) => {
  if (value === undefined) return fallback;
  return JSON.parse(JSON.stringify(value));
};

const stripKeys = (obj, keys) => {
  if (!obj || typeof obj !== "object") return obj;
  const next = { ...obj };
  keys.forEach(k => { delete next[k]; });
  return next;
};

const sanitizeDiscount = (discount) => {
  if (!discount) return null;
  return stripKeys(clone(discount, null), ["key"]);
};

const sanitizeLineItem = (item) => {
  const next = stripKeys(clone(item, {}), ["key"]);
  if (next.discount) next.discount = sanitizeDiscount(next.discount);
  return next;
};

const sanitizeQuoteDiscount = (discount) => stripKeys(clone(discount, {}), ["key"]);

const sanitizeAttachment = (attachment) => stripKeys(clone(attachment, {}), ["dataUrl"]);

export function stripSessionOperational(session) {
  if (!session || typeof session !== "object") return session;
  const next = {};
  SESSION_COMMERCIAL_FIELDS.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(session, field)) next[field] = clone(session[field], session[field]);
  });
  OPERATIONAL_SESSION_FIELDS.forEach(field => { delete next[field]; });
  return next;
}

/* The versions that are amendments. A version the AI box keeps stays in version history
   and can be restored, but it isn't an amendment, so job cards don't count or label it. */
export const amendmentVersions = (versions) => (versions || []).filter((v) => v?.source !== "ai-edit");

export function snapshotQuoteForVersion(quote, meta = {}) {
  const version = {
    schemaVersion: SCHEMA_VERSION,
    id: meta.id || mkVersionId(),
    timestamp: meta.timestamp || new Date().toISOString(),
    source: meta.source || "snapshot",
    note: meta.note || "",
    restoredFromVersionId: meta.restoredFromVersionId || null,
    preRestoreOfVersionId: meta.preRestoreOfVersionId || null,
    total: Number(quote?.total) || 0,
  };

  QUOTE_FIELDS.forEach(field => {
    if (field === "lineItems") {
      version.lineItems = (quote?.lineItems || []).map(sanitizeLineItem);
    } else if (field === "lineGroups") {
      // The one reader every load path uses: the persisted list, or the derived one for
      // a quote that predates it, so the snapshot never carries a stale empty array.
      version.lineGroups = quoteLineGroups(quote);
    } else if (field === "quoteDiscounts") {
      version.quoteDiscounts = (quote?.quoteDiscounts || []).map(sanitizeQuoteDiscount);
    } else if (field === "sessions") {
      version.sessions = sortSessions(quote?.sessions || []).map(stripSessionOperational);
    } else if (field === "attachments") {
      version.attachments = (quote?.attachments || []).map(sanitizeAttachment);
    } else if (field === "sections") {
      version.sections = clone(quote?.sections || initSections(), initSections());
    } else if (field === "billing") {
      version.billing = normalizeBilling(quote?.billing);
    } else {
      version[field] = clone(quote?.[field], defaultForField(field));
    }
  });

  // The old clientId isn't versioned and outranks companyEntityId at booking, so the client
  // this quote was really on goes in the field a restore brings back.
  if (quote?.clientId) version.companyEntityId = quote.clientId;
  return version;
}

function defaultForField(field) {
  if (["lineItems", "lineGroups", "quoteDiscounts", "sessions", "attachments", "equipmentIds", "crew"].includes(field)) return [];
  if (field === "sections") return initSections();
  if (["multiDay", "scanDateTBD", "isStudio", "turnaroundTBD", "requiresDeposit"].includes(field)) return false;
  // Off, on or automatic: never set means automatic, and false would hide the sheet.
  if (field === "includePricingSheet") return null;
  if (field === "deliverableFormats") return ["obj"];
  if (field === "deposit") return { type: "none", value: null };
  if (field === "approvedClientCode" || field === "printWording" || field === "printWordingMoneyKey") return null;
  if (field === "clientUnlinked") return false;
  return "";
}

export function applyVersionToQuote(currentQuote, version) {
  const restored = { ...currentQuote };
  // The old clientId isn't versioned and outranks companyEntityId at booking, so left here
  // it would book the restored client under today's. Null, because saves merge.
  if (currentQuote?.clientId) restored.clientId = null;
  QUOTE_FIELDS.forEach(field => {
    if (field === "quoteDate") return;
    if (field === "lineItems") {
      restored.lineItems = (version.lineItems || []).map((li, i) => ({ ...sanitizeLineItem(li), key: Date.now() + i }));
    } else if (field === "lineGroups") {
      // The version's own blocks, never the current quote's: a version that predates
      // the field derives its list from ITS OWN prefill labels, line groups and notes.
      // The notes map follows the restored list.
      restored.lineGroups = versionLineGroups(version);
      restored.lineGroupNotes = lineGroupNotesFrom(restored.lineGroups);
    } else if (field === "quoteDiscounts") {
      restored.quoteDiscounts = (version.quoteDiscounts || []).map((disc, i) => ({ ...sanitizeQuoteDiscount(disc), key: `qd_${Date.now()}_${i}` }));
    } else if (field === "sessions") {
      restored.sessions = sortSessions(version.sessions || []).map(stripSessionOperational);
    } else if (field === "attachments") {
      restored.attachments = (version.attachments || []).map(sanitizeAttachment);
    } else if (field === "sections") {
      restored.sections = clone(version.sections || initSections(), initSections());
      // What a version doesn't hold, a whole section or just its text or switch, prints from
      // the default. Written out, because saves merge nested maps and newer wording would
      // otherwise stay under it.
      for (const [key, def] of Object.entries(SECTION_DEFS)) {
        const held = restored.sections[key];
        restored.sections = { ...restored.sections, [key]: { ...held, enabled: held?.enabled ?? !!def.defaultOn, text: held?.text ?? def.defaultText ?? "" } };
      }
      // Saves merge nested maps: a version with no operatorSet has to say so, or a newer
      // operator choice stays underneath and travel stops following the schedule.
      if (restored.sections?.travel && !restored.sections.travel.operatorSet) {
        restored.sections = { ...restored.sections, travel: { ...restored.sections.travel, operatorSet: false } };
      }
    } else if (field === "equipmentIds" || field === "crew") {
      // Versions snapshotted before holds existed carry neither field: keep the
      // current assignments rather than restoring an empty list over them.
      restored[field] = version[field] === undefined
        ? clone(currentQuote?.[field], [])
        : clone(version[field], []);
    } else if (field === "deposit") {
      // Versions snapshotted before the structured deposit existed carry only
      // requiresDeposit — synthesize the same legacy-marked shape migrateQuote
      // stamps, so restoring an old version doesn't silently erase its deposit
      // (or change its printed payment sentence).
      restored.deposit = version.deposit
        // legacy off in so many words when the current one has it: saves merge nested maps.
        ? { ...clone(version.deposit, defaultForField("deposit")), ...(currentQuote?.deposit && "legacy" in currentQuote.deposit && !version.deposit.legacy ? { legacy: false } : {}) }
        : { type: version.requiresDeposit ? "full_session" : "none", value: null, legacy: true };
    } else if (field === "printWording") {
      restored.printWording = normalizePrintWording(version.printWording);
    } else if (field === "billing") {
      // A version from before billing was versioned has none, and a discount's name
      // doesn't prove it (proBono.test.js): keep today's flag and don't guess. The
      // restore screen says to check billing when such a version has a Pro bono discount.
      if (version.billing !== undefined) restored.billing = normalizeBilling(version.billing);
    } else if (field === "contactPhone") {
      // A version from before the phone was versioned: the current phone only if it is
      // the same person, so a restore can't put one person's phone beside another's
      // name. restoreContactFields fills it from the CRM when it can.
      if (version.contactPhone !== undefined) restored.contactPhone = clone(version.contactPhone, "");
      else if ((version.contactEntityId || null) !== (currentQuote?.contactEntityId || null)) restored.contactPhone = "";
    } else {
      restored[field] = clone(version[field], defaultForField(field));
    }
  });

  PRESERVE_FIELDS.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(currentQuote || {}, field)) {
      restored[field] = currentQuote[field];
    }
  });
  if (!restored.clientCode && restored.clientName) restored.clientCode = mkClientCode(restored.clientName);
  restored.savedToDb = true;
  restored.step = currentQuote?.step || "review";

  const totals = calcTotals(restored.lineItems || [], restored.quoteDiscounts || []);
  return {
    ...restored,
    subtotal: totals.subtotal,
    totalSavings: totals.totalSavings,
    total: totals.total,
    mathVerified: true,
  };
}

// The block list a version describes: its snapshotted lineGroups when it has them, else
// the list derived from the version's own lidarPrefill labels, line groups and
// lineGroupNotes (a version snapshotted before the field existed). Never reads the
// current quote. Exported so the history modal previews the same list a restore applies.
export function versionLineGroups(version) {
  return quoteLineGroups(version || {});
}

export function isLegacyVersion(version) {
  return version?.schemaVersion !== SCHEMA_VERSION;
}

export function isMarkerVersion(version) {
  return version?.source === "post-restore-marker" || !!version?.markerOnly;
}

export function summarizeVersion(version) {
  return {
    id: version?.id || "",
    timestamp: version?.timestamp || "",
    total: Number(version?.total) || 0,
    source: version?.source || "legacy",
    note: version?.note || "",
    lineItemCount: Array.isArray(version?.lineItems) ? version.lineItems.length : 0,
    sessionCount: Array.isArray(version?.sessions) ? version.sessions.length : 0,
    // The version's blocks, in print order (a pre-field version derives its own list).
    blockLabels: versionLineGroups(version).map(g => g.label),
    isLegacy: isLegacyVersion(version),
    isMarker: isMarkerVersion(version),
  };
}

const sessionKey = (s) => `${s?.date || ""}|${s?.sessionType || ""}`;
const locationSig = (s) => [
  s?.isStudio ? "studio" : "location",
  s?.location || s?.jobLocation || "",
  s?.locationVenue || "",
  s?.locationAddress || "",
  s?.locationCity || "",
  s?.locationState || "",
  s?.locationZip || "",
].join("|");
const scheduleSig = (s) => [s?.dayType || "", s?.callTime || "", s?.wrapTime || "", locationSig(s)].join("|");

export function compareQuoteVsJobSessions(quoteSessions, jobSessions) {
  const quote = sortSessions(quoteSessions || []);
  const job = sortSessions(jobSessions || []);
  const jobByKey = new Map();
  job.forEach(s => {
    const key = sessionKey(s);
    if (!jobByKey.has(key)) jobByKey.set(key, []);
    jobByKey.get(key).push(s);
  });

  const matches = [];
  const quoteOnly = [];
  const differing = [];
  const usedJobIds = new Set();

  quote.forEach(qs => {
    const candidates = jobByKey.get(sessionKey(qs)) || [];
    const js = candidates.find(s => !usedJobIds.has(s.id || `${sessionKey(s)}:${job.indexOf(s)}`));
    if (!js) {
      quoteOnly.push(qs);
      return;
    }
    usedJobIds.add(js.id || `${sessionKey(js)}:${job.indexOf(js)}`);
    if (scheduleSig(qs) === scheduleSig(js)) matches.push({ quote: qs, job: js });
    else differing.push({ quote: qs, job: js });
  });

  const jobOnly = job.filter((js, i) => !usedJobIds.has(js.id || `${sessionKey(js)}:${i}`));
  return {
    matches,
    quoteOnly,
    jobOnly,
    differing,
  };
}

const money = (value) => fmtMoney(value || 0);
const canonical = (value) => JSON.stringify(clone(value, null));
const canonicalLineItems = (items) => canonical((items || []).map(sanitizeLineItem));
const hasCalendarEvents = (job) => (job?.sessions || []).some(s => s?.gcalEventId || s?.gcalSyncedFingerprint) || !!(job?.gcalEventId || job?.gcalSyncedFingerprint);
const hasDeliveredCaptureRows = (job) => (
  (job?.captureLog || []).some(r => r?.delivered) ||
  (job?.sessions || []).some(s => (s?.captureLog || []).some(r => r?.delivered))
);
const hasDeliveryProgress = (job) => !!(
  job?.deliveredDate ||
  job?.deliveryDate ||
  (job?.deliveryMethods || []).length ||
  job?.deliverableRequirements ||
  (job?.files || []).length ||
  (job?.jobDocs || []).length ||
  hasDeliveredCaptureRows(job)
);

export function computeQuoteRestoreImpact({ currentQuote, targetVersion, linkedJob, recomputedTotal }) {
  const pendingQuote = targetVersion && !isLegacyVersion(targetVersion) && !isMarkerVersion(targetVersion)
    ? applyVersionToQuote(currentQuote || {}, targetVersion)
    : null;
  const total = Number(recomputedTotal ?? pendingQuote?.total ?? targetVersion?.total ?? 0);
  const liveJob = linkedJob && !linkedJob.deletedAt ? linkedJob : null;
  const currentTotal = Number(liveJob?.grossRevenue ?? currentQuote?.total ?? 0);
  const revenueDelta = liveJob ? { from: currentTotal, to: total, delta: total - currentTotal } : null;
  const willChange = [
    "Quote pricing, scope, line items, blocks, discounts, summary, deliverables, terms, sections, notes, schedule, and location fields.",
    `Quote total will be restored to ${money(total)}.`,
  ];
  const willNotChange = [
    "Live job sessions will not be changed.",
    "Completed capture logs, files, crew, actual arrival and wrap times, processing progress, delivery progress, calendar events, invoices, payments, booking status, and follow-up history will not be changed.",
    "Change-order relationships and job linkage will stay as-is.",
  ];
  const needsReview = [];
  const blockers = [];

  if (isLegacyVersion(targetVersion)) blockers.push("This is a legacy version and is view-only.");
  if (isMarkerVersion(targetVersion)) blockers.push("This restore audit marker is view-only.");
  if (currentQuote?.changeOrderOfJobId || currentQuote?.changeOrderOfQuoteId || targetVersion?.changeOrderOfJobId || targetVersion?.changeOrderOfQuoteId) {
    blockers.push("Change-order quote restore is not available in this release.");
  }

  if (liveJob) {
    const revenueMessage = `This will change gross revenue on job ${liveJob.jobId || liveJob.id} from ${money(currentTotal)} to ${money(total)}.`;
    if (liveJob.paid || liveJob.paidDate) {
      needsReview.push(`This job is paid. Type RESTORE to continue. Gross revenue will not update automatically. Currently ${money(currentTotal)}; restored quote total is ${money(total)}.`);
    } else if (liveJob.invoiced || liveJob.invoiceDate) {
      needsReview.push(`This job is invoiced. Confirm before restoring. Gross revenue will not update automatically. Currently ${money(currentTotal)}; restored quote total is ${money(total)}.`);
    } else {
      willChange.push(revenueMessage);
    }

    const sessionCompare = compareQuoteVsJobSessions(pendingQuote?.sessions || targetVersion?.sessions || [], liveJob.sessions || []);
    if (sessionCompare.quoteOnly.length || sessionCompare.jobOnly.length || sessionCompare.differing.length) {
      needsReview.push(`The restored quote has ${(pendingQuote?.sessions || targetVersion?.sessions || []).length} quoted sessions; the live job has ${(liveJob.sessions || []).length} job sessions. Job sessions will not be changed.`);
    }
    if (sessionCompare.differing.length) needsReview.push("Quoted session time or location differs from live job sessions.");
    const completed = (liveJob.sessions || []).some(sessionHasCaptureData) || sessionHasCaptureData(liveJob);
    if (completed) needsReview.push("This job has completed capture activity. Completed work will not be changed.");
    if (completed && canonicalLineItems(currentQuote?.lineItems || []) !== canonicalLineItems(pendingQuote?.lineItems || targetVersion?.lineItems || [])) {
      needsReview.push("Completed capture activity exists and restored line items differ from the current quote.");
    }
    if (hasCalendarEvents(liveJob)) needsReview.push("Calendar events exist on the live job. Restore will not update Google Calendar events.");
    if (hasDeliveryProgress(liveJob)) needsReview.push("Delivery docs, files, or progress may already exist. Restore will not change them.");
    if (canonical(liveJob.quotedTerms || null) !== canonical(pendingQuote?.sections || targetVersion?.sections || null)) {
      needsReview.push("Delivery docs may continue using job-side terms/deliverables until manually updated.");
    }
    if (canonical(liveJob.deliverableFormats || []) !== canonical(pendingQuote?.deliverableFormats || targetVersion?.deliverableFormats || [])) {
      needsReview.push("Job-side deliverable formats differ from the restored quote.");
    }
  }

  if (targetVersion && targetVersion.billing === undefined && hasProBonoDiscount(targetVersion.quoteDiscounts)) {
    needsReview.push("This version has a discount named Pro bono but doesn't record billing. Restoring keeps this quote's current billing. Check billing after restoring.");
  }

  const attachments = targetVersion?.attachments || [];
  if (attachments.some(a => a?.type === "image" && !a.url)) {
    needsReview.push("Some attachment metadata has no durable URL. Attachment recovery is best-effort.");
  }

  return { willChange, willNotChange, needsReview, blockers, revenueDelta };
}

/* ═══ VERSION DATES (2026-09-13, Codex review of the Financials tab) ═══
   A version's `timestamp` is an EVENT time: when the amend opened (the
   snapshot of the pre-amend content) or when a restore ran (the audit marker).
   It is never the date the archived content was in force from. The job card's
   Financials tab used to print that event time on archived rows next to the
   editable quoteDate on the current row, in one unlabeled column, so after an
   amendment the current version read older than the archived one.

   The current version came into effect at the LAST transition event on the
   quote: the newest-stamped entry in `versions`, whatever its source (an amend
   snapshot or a restore marker both mark a transition). Newest by PARSED
   timestamp, not by array position or string order, so an out-of-order legacy
   array, a mixed precision, or an offset form still answers correctly and an
   unparseable stamp is skipped (GO review 2026-09-13). With no history, `savedAt`
   is the best fallback the data offers ("last saved", not "version started");
   the caller falls back further (quoteDate, createdAt) for legacy records. */
export function currentVersionSince(quote) {
  const versions = Array.isArray(quote?.versions) ? quote.versions : [];
  let newest = "";
  let newestMs = -Infinity;
  for (const v of versions) {
    const ts = typeof v?.timestamp === "string" ? v.timestamp : "";
    const ms = ts ? new Date(ts).getTime() : NaN;
    if (Number.isFinite(ms) && ms > newestMs) { newest = ts; newestMs = ms; }
  }
  return newest || quote?.savedAt || null;
}

/* The calendar DAY of an event timestamp, in the viewer's own timezone, as
   YYYY-MM-DD for fmtDate. `String(iso).slice(0, 10)` took the UTC day, so an
   amendment at 10 pm in New York printed tomorrow's date. A bare YYYY-MM-DD
   (quoteDate, captureDate) has no time and passes through untouched. */
export function eventDay(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (!s.includes("T")) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
